import { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { EnhancedHeader } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft,
  LayoutDashboard,
  Check,
  Images,
  AlertCircle,
  Loader2,
  RefreshCw,
  ShoppingCart,
  Coins,
  Gem
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { usePurchaseItem } from '@/hooks/use-currency';

interface BoardItem {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  itemType: string;
  category: string;
  isActive: boolean;
  price: number;
  currencyType: 'regular' | 'premium';
}

interface UserBoardSettings {
  boardId: number | null;
  isDefault: boolean;
}

export function BoardDetailsPage() {
  const [_, params] = useRoute<{ id: string }>('/board/:id');
  const [__, setLocation] = useLocation();
  const { toast } = useToast();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const boardId = params?.id ? parseInt(params.id) : null;

  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isCurrentDefault, setIsCurrentDefault] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);

  // Fetch board details
  const {
    data: board,
    isLoading: boardLoading,
    isError: boardError
  } = useQuery({
    queryKey: ['board', boardId],
    queryFn: async () => {
      if (!boardId) return null;
      const response = await apiRequest('GET', `/api/customization/shopItem/${boardId}`);
      return response.json() as Promise<BoardItem>;
    },
    enabled: !!boardId && !!token
  });

  // Fetch user's current board settings
  const {
    data: userSettings,
    isLoading: settingsLoading
  } = useQuery({
    queryKey: ['user-board-settings'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/game-boards/settings');
      return response.json() as Promise<UserBoardSettings>;
    },
    enabled: !!token
  });

  // Fetch user's purchases to check if they own this board
  const {
    data: purchases,
    isLoading: purchasesLoading
  } = useQuery({
    queryKey: ['user-purchases'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/currency/purchases');
      return response.json();
    },
    enabled: !!token
  });

  // Check if user owns this board
  const isOwned = purchases?.some((purchase: any) => 
    (purchase.shopItemId === boardId || purchase.item?.id === boardId) && 
    purchase.isActive !== false
  );

  useEffect(() => {
    if (userSettings && board) {
      setIsCurrentDefault(userSettings.boardId === board.id);
    }
  }, [userSettings, board]);

  // Purchase mutation
  const { mutate: purchaseItem } = usePurchaseItem();

  // Set board as default mutation
  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!boardId) throw new Error("No board ID provided");
      
      await apiRequest('POST', '/api/game-boards/set', {
        boardId
      });
    },
    onMutate: () => {
      setIsSettingDefault(true);
    },
    onSuccess: () => {
      setIsCurrentDefault(true);
      toast({
        title: "Background Set",
        description: "This board has been set as your game room background.",
        variant: "default"
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['user-board-settings'] });
    },
    onError: (error) => {
      console.error('Error setting board background:', error);
      toast({
        title: "Error",
        description: "Failed to set board as background. Please try again.",
        variant: "destructive"
      });
    },
    onSettled: () => {
      setIsSettingDefault(false);
    }
  });

  const handleSetDefault = () => {
    if (!isCurrentDefault && isOwned) {
      setDefaultMutation.mutate();
    }
  };

  const handlePurchase = () => {
    if (!board || isOwned) return;
    setShowPurchaseConfirm(true);
  };

  const confirmPurchase = () => {
    if (!board || isOwned) return;
    
    setIsPurchasing(true);
    setShowPurchaseConfirm(false);
    purchaseItem(board.id, {
      onSuccess: () => {
        toast({
          title: "Purchase successful!",
          description: "You can now use this board as your game room background.",
        });
        queryClient.invalidateQueries({ queryKey: ['user-purchases'] });
      },
      onError: (error: any) => {
        let errorMessage = "Failed to complete purchase.";
        if (error.message) {
          errorMessage = error.message;
        }
        toast({
          title: "Purchase failed",
          description: errorMessage,
          variant: "destructive",
        });
      },
      onSettled: () => {
        setIsPurchasing(false);
      }
    });
  };

  if (boardLoading || settingsLoading || purchasesLoading) {
    return (
      <div className="min-h-screen themed-gradient text-white">
        <EnhancedHeader
          title="Board Details"
          backPath="/settings"
          coloredText={true}
        />
        
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="h-10 w-10 text-primary mx-auto animate-spin" />
            <p className="mt-4 text-white/70">Loading board details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (boardError || !board) {
    return (
      <div className="min-h-screen themed-gradient text-white">
        <EnhancedHeader
          title="Board Details"
          backPath="/settings"
          coloredText={true}
        />
        
        <div className="p-4 max-w-md mx-auto">
          <Card className="bg-red-900/20 border-red-700/50 text-white">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
              <h2 className="text-xl font-bold mb-2">Board Not Found</h2>
              <p className="text-white/70 mb-6">The board you're looking for could not be found or may have been removed.</p>
              
              <Button 
                onClick={() => setLocation("/settings")}
                variant="outline"
                className="border-red-500/30 hover:bg-red-900/20"
              >
                Back to Settings
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen themed-gradient text-white pb-20">
      <EnhancedHeader
        title="Board Details"
        backPath="/settings"
        coloredText={true}
        showBackButton={true}
      />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Board title and status */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{board.name}</h1>
              <p className="text-white/70 text-sm">{board.description}</p>
            </div>
            <Badge 
              className={cn(
                "text-sm px-3 py-1",
                isCurrentDefault 
                  ? "bg-green-600/80 hover:bg-green-600 text-white" 
                  : "bg-gray-700/80 text-gray-200"
              )}
            >
              {isCurrentDefault ? 'Current Background' : 'Not Active'}
            </Badge>
          </div>

          <Separator className="bg-white/10" />

          {/* Board preview */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Images className="h-5 w-5 text-primary" />
              <span>Board Preview</span>
            </h2>

            <div className="relative px-4 py-4 rounded-lg overflow-hidden  bg-black/20 border border-white/10">
              <img 
                src={board.imageUrl} 
                alt={board.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="text-sm text-white/60 flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              <span>This is how the background will appear in game rooms</span>
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* Board information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Board Information</h2>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/70">Type:</span>
                  <span className="font-medium">{board.itemType.charAt(0).toUpperCase() + board.itemType.slice(1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Category:</span>
                  <span className="font-medium">{board.category.charAt(0).toUpperCase() + board.category.slice(1)}</span>
                </div>
                {!isOwned && (
                  <div className="flex justify-between">
                    <span className="text-white/70">Price:</span>
                    <span className="font-medium flex items-center gap-1">
                      {board.price}
                      {board.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-400" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-400" />
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Actions</h2>
              
              <div className="space-y-4">
                {isOwned ? (
                  <Button
                    onClick={handleSetDefault}
                    disabled={isSettingDefault || isCurrentDefault}
                    className={cn(
                      "w-full",
                      isCurrentDefault && "bg-green-800/50 hover:bg-green-800/50 text-white/70"
                    )}
                  >
                    {isSettingDefault ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Setting as Background...
                      </>
                    ) : isCurrentDefault ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Current Background
                      </>
                    ) : (
                      <>
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Set as Game Room Background
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handlePurchase}
                    disabled={isPurchasing}
                    className="w-full"
                  >
                    {isPurchasing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Purchasing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Purchase Board ({board.price} {board.currencyType === 'premium' ? 'Gems' : 'Coins'})
                      </>
                    )}
                  </Button>
                )}
                
                <Button
                  onClick={() => setLocation("/settings")}
                  variant="outline"
                  className="w-full"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Settings
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={showPurchaseConfirm} onOpenChange={setShowPurchaseConfirm}>
        <DialogContent className="bg-gray-900 border border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Purchase</DialogTitle>
            <DialogDescription className="text-gray-300">
              Are you sure you want to purchase this board?
            </DialogDescription>
          </DialogHeader>

          {board && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-white">
                  {board.name}
                </span>
                <div className="flex items-center gap-1 text-white">
                  <span>{board.price}</span>
                  {board.currencyType === "premium" ? (
                    <Gem className="h-4 w-4 text-purple-400" />
                  ) : (
                    <Coins className="h-4 w-4 text-amber-400" />
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                {board.description}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPurchaseConfirm(false)}
              disabled={isPurchasing}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmPurchase}
              disabled={isPurchasing}
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Confirm Purchase
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}